import { useMemo, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';

import Alert from '@mui/material/Alert';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import MenuItem from '@mui/material/MenuItem';
import Popover from '@mui/material/Popover';
import Stack from '@mui/material/Stack';
import TablePagination from '@mui/material/TablePagination';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import ClearIcon from '@mui/icons-material/Clear';
import DiamondOutlinedIcon from '@mui/icons-material/DiamondOutlined';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import SearchIcon from '@mui/icons-material/Search';

import MainCard from 'components/MainCard';
import API_BASE_URL from 'config/api';
import useGet from 'hooks/useGet';

const statusColorMap = {
  created: 'default',
  pending: 'warning',
  authorized: 'info',
  captured: 'success',
  failed: 'error',
  cancelled: 'default',
  refunded: 'secondary'
};

const orderStatusColorMap = {
  pending: 'warning',
  placed: 'info',
  processing: 'info',
  packed: 'secondary',
  readytoship: 'secondary',
  shipped: 'primary',
  delivered: 'success'
};

const paymentStatuses = ['created', 'pending', 'authorized', 'captured', 'failed', 'cancelled', 'refunded'];
const orderStatuses = ['pending', 'placed', 'processing', 'packed', 'readytoship', 'shipped', 'delivered'];

const toTitleCase = (value) =>
  String(value || '')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/[-_]/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());

const formatMoney = (currency, amount) => {
  const code = String(currency || 'INR').toUpperCase();
  const value = Number(amount || 0);
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: code,
      maximumFractionDigits: 2
    }).format(value);
  } catch {
    return `${code} ${value.toFixed(2)}`;
  }
};

const toDateInputValue = (date) => {
  const value = new Date(date);
  if (Number.isNaN(value.getTime())) return '';

  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, '0');
  const day = String(value.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getDateRangePreset = (preset) => {
  const today = new Date();
  const from = new Date(today);

  if (preset === 'week') {
    from.setDate(today.getDate() - 6);
  }
  if (preset === 'month') {
    from.setDate(1);
  }

  return {
    from: toDateInputValue(from),
    to: toDateInputValue(today)
  };
};

const getCustomerName = (order) =>
  order?.user?.name ||
  order?.customerName ||
  order?.name ||
  order?.personalization?.name ||
  order?.billingAddress?.name ||
  order?.shippingAddress?.name ||
  order?.user?.email ||
  '-';

const getProductName = (order) => {
  if (order?.productName) return order.productName;
  if (order?.product?.name) return order.product.name;
  if (order?.product?.title) return order.product.title;
  if (Array.isArray(order?.items) && order.items.length > 0) {
    const titles = order.items
      .map((item) => item?.title || item?.product?.title || item?.product?.name || item?.productVariant?.title)
      .filter(Boolean);
    if (titles.length) return titles.join(', ');
  }
  return '-';
};

const getCollection = (payload) => {
  if (Array.isArray(payload)) return payload;
  return payload?.products || payload?.data || payload?.items || [];
};

const resolveProductImageUrl = (product) => {
  const raw =
    product?.image ||
    product?.imageUrl ||
    product?.thumbnail ||
    product?.thumbnailUrl ||
    product?.featuredImage ||
    product?.coverImage ||
    product?.heroImage ||
    product?.productImage ||
    product?.media?.heroImage ||
    product?.media?.galleryImages?.[0] ||
    product?.galleryImages?.[0] ||
    product?.images?.[0]?.url ||
    product?.images?.[0]?.path ||
    product?.images?.[0];

  if (!raw) return '';
  if (typeof raw !== 'string') {
    if (typeof raw === 'object') {
      const candidate = raw.url || raw.path || raw.src || '';
      if (typeof candidate === 'string' && candidate) {
        return resolveProductImageUrl({ image: candidate });
      }
    }
    return '';
  }

  const value = raw.trim().replace(/\\/g, '/');
  if (!value) return '';
  if (/^(https?:|blob:|data:)/i.test(value)) return value;

  const normalized = value.replace(/^\/+/, '');
  if (normalized.startsWith('uploads/')) return `${API_BASE_URL}/${normalized}`;
  if (normalized.startsWith('media/')) return `${API_BASE_URL}/uploads/${normalized}`;
  if (value.startsWith('/')) return `${API_BASE_URL}${value}`;
  return `${API_BASE_URL}/uploads/${normalized}`;
};

const createProductLookup = (products) => {
  const lookup = new Map();

  getCollection(products).forEach((product) => {
    [
      product?._id,
      product?.id,
      product?.productId,
      product?.sku,
      product?.SKU,
      product?.name,
      product?.title,
      product?.productName
    ].forEach((key) => {
      if (key) lookup.set(String(key).toLowerCase().trim(), product);
    });
  });

  return lookup;
};

const findCatalogProduct = (lookup, item) => {
  if (!lookup) return null;
  const keys = [
    item?.productId,
    item?._id,
    item?.id,
    item?.sku,
    item?.SKU,
    item?.name,
    item?.title,
    item?.productName,
    item?.product?.title,
    item?.product?.name,
    item?.product?._id,
    item?.product?.sku,
    item?.product?.SKU
  ];
  for (const key of keys) {
    if (key) {
      const match = lookup.get(String(key).toLowerCase().trim());
      if (match) return match;
    }
  }
  return null;
};

const getProductImage = (order, productLookup) => {
  // 1. Direct order image
  const directOrderImg = resolveProductImageUrl(order);
  if (directOrderImg) return directOrderImg;

  // 2. Direct item images
  const items = Array.isArray(order?.items) ? order.items : Array.isArray(order?.products) ? order.products : [];
  for (const item of items) {
    const itemImg =
      resolveProductImageUrl(item) ||
      resolveProductImageUrl(item?.product) ||
      resolveProductImageUrl(item?.productVariant) ||
      resolveProductImageUrl(item?.variant);
    if (itemImg) return itemImg;
  }

  // 3. Look up in catalog
  if (productLookup) {
    const catalogMatch =
      (items.length > 0 ? findCatalogProduct(productLookup, items[0]) : null) ||
      findCatalogProduct(productLookup, order) ||
      (order?.productName ? findCatalogProduct(productLookup, { title: order.productName }) : null) ||
      (getProductName(order) !== '-' ? findCatalogProduct(productLookup, { title: getProductName(order) }) : null);

    if (catalogMatch) {
      const catalogImg = resolveProductImageUrl(catalogMatch);
      if (catalogImg) return catalogImg;
    }
  }

  return null;
};

const formatDateDisplay = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  }).format(date);
};

const getDispatchStatus = (order) => {
  const status = order?.orderStatus || order?.dispatchStatus || order?.deliveryStatus || order?.shippingStatus || order?.trackingStatus;
  if (status) return toTitleCase(status);
  return order?.trackingNo || order?.trackingId ? 'Dispatched' : 'Pending';
};

export default function PaymentsOrders() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [paymentStatus, setPaymentStatus] = useState('');
  const [orderStatus, setOrderStatus] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [dateFilterMode, setDateFilterMode] = useState('');
  const [filterAnchor, setFilterAnchor] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  const endpoint = useMemo(() => {
    const q = search.trim();
    const params = new URLSearchParams();
    params.set('page', String(page + 1));
    params.set('limit', String(rowsPerPage));
    if (q) params.set('q', q);
    if (paymentStatus) params.set('paymentStatus', paymentStatus);
    if (orderStatus) params.set('orderStatus', orderStatus);
    if (fromDate) params.set('from', fromDate);
    if (toDate) params.set('to', toDate);
    const query = params.toString();
    return query ? `/api/payments/admin/orders?${query}` : '/api/payments/admin/orders';
  }, [search, paymentStatus, orderStatus, fromDate, toDate, page, rowsPerPage]);

  const { data, error, loading } = useGet(endpoint);
  const { data: productCatalogResponse } = useGet('/api/products/?limit=all');
  const productLookup = useMemo(() => createProductLookup(productCatalogResponse), [productCatalogResponse]);
  const orders = data?.orders || [];
  const hasDateFilter = Boolean(fromDate || toDate);
  const activeFilterCount = [search.trim(), paymentStatus, orderStatus, hasDateFilter].filter(Boolean).length;
  const popoverFilterCount = [paymentStatus, orderStatus, hasDateFilter].filter(Boolean).length;
  const dateFilterLabel =
    dateFilterMode === 'day'
      ? 'Day'
      : dateFilterMode === 'week'
        ? 'Week'
        : dateFilterMode === 'month'
          ? 'Month'
          : hasDateFilter
            ? 'Date range'
            : 'Date filter';

  const filterOpen = Boolean(filterAnchor);

  const applyDatePreset = (preset) => {
    const range = getDateRangePreset(preset);
    setFromDate(range.from);
    setToDate(range.to);
    setDateFilterMode(preset);
    setPage(0);
  };

  const clearDateFilter = () => {
    setFromDate('');
    setToDate('');
    setDateFilterMode('');
    setPage(0);
  };

  const clearFilters = () => {
    setSearch('');
    setPaymentStatus('');
    setOrderStatus('');
    clearDateFilter();
    setPage(0);
  };

  const clearPopoverFilters = () => {
    setPaymentStatus('');
    setOrderStatus('');
    clearDateFilter();
  };

  return (
    <Stack spacing={2.5} sx={{ maxWidth: '100%' }}>
      <MainCard
        title="Filters"
        secondary={<Chip size="small" label={activeFilterCount ? `${activeFilterCount} active` : 'No filters'} variant="outlined" />}
      >
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', md: 'center' }}>
          <TextField
            fullWidth
            size="small"
            label="Search by order/payment id"
            value={search}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              )
            }}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(0);
            }}
          />
          <Stack direction="row" spacing={1} alignItems="center">
            <Tooltip title="Filters">
              <IconButton
                color={popoverFilterCount ? 'primary' : 'default'}
                onClick={(event) => setFilterAnchor(event.currentTarget)}
                sx={{
                  border: '1px solid',
                  borderColor: popoverFilterCount ? 'primary.main' : 'divider',
                  width: 40,
                  height: 40
                }}
              >
                <FilterAltOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Chip
              size="small"
              label={popoverFilterCount ? `${popoverFilterCount} filter${popoverFilterCount > 1 ? 's' : ''}` : 'Add filter'}
              color={popoverFilterCount ? 'primary' : 'default'}
              variant="outlined"
            />
          </Stack>
          <Popover
            open={filterOpen}
            anchorEl={filterAnchor}
            onClose={() => setFilterAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            slotProps={{ paper: { sx: { mt: 1, p: 2, width: 320 } } }}
          >
            <Stack spacing={1.5}>
              <Typography variant="subtitle2">Add Filter</Typography>
              <TextField
                select
                size="small"
                label="Payment Status"
                value={paymentStatus}
                onChange={(e) => {
                  setPaymentStatus(e.target.value);
                  setPage(0);
                }}
                fullWidth
              >
                <MenuItem value="">All payment statuses</MenuItem>
                {paymentStatuses.map((status) => (
                  <MenuItem key={status} value={status}>
                    {toTitleCase(status)}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                size="small"
                label="Order Status"
                value={orderStatus}
                onChange={(e) => {
                  setOrderStatus(e.target.value);
                  setPage(0);
                }}
                fullWidth
              >
                <MenuItem value="">All order statuses</MenuItem>
                {orderStatuses.map((status) => (
                  <MenuItem key={status} value={status}>
                    {toTitleCase(status)}
                  </MenuItem>
                ))}
              </TextField>
              <Typography variant="caption" color="text.secondary">
                {dateFilterLabel}
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {[
                  ['day', 'Day'],
                  ['week', 'Week'],
                  ['month', 'Month']
                ].map(([value, label]) => (
                  <Button
                    key={value}
                    size="small"
                    variant={dateFilterMode === value ? 'contained' : 'outlined'}
                    onClick={() => applyDatePreset(value)}
                  >
                    {label}
                  </Button>
                ))}
              </Stack>
              <TextField
                size="small"
                type="date"
                label="From"
                value={fromDate}
                onChange={(e) => {
                  setFromDate(e.target.value);
                  setDateFilterMode('range');
                  setPage(0);
                }}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
              <TextField
                size="small"
                type="date"
                label="To"
                value={toDate}
                onChange={(e) => {
                  setToDate(e.target.value);
                  setDateFilterMode('range');
                  setPage(0);
                }}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
              <Stack direction="row" spacing={1} justifyContent="space-between">
                <Button size="small" color="inherit" onClick={clearPopoverFilters} disabled={!popoverFilterCount}>
                  Clear filters
                </Button>
                <Button size="small" variant="contained" onClick={() => setFilterAnchor(null)}>
                  Apply
                </Button>
              </Stack>
            </Stack>
          </Popover>
          <Button
            variant="outlined"
            color="inherit"
            startIcon={<ClearIcon />}
            disabled={!activeFilterCount}
            onClick={clearFilters}
            sx={{ minWidth: 130 }}
          >
            Clear
          </Button>
        </Stack>
      </MainCard>

      {loading && <Typography>Loading payment orders...</Typography>}
      {error && <Alert severity="error">Failed to load payment orders.</Alert>}

      {!loading && !error && (
        <MainCard title={`Orders (${Number(data?.total || orders.length || 0)})`} contentSX={{ p: { xs: 1.5, sm: 2 } }}>
          <Stack spacing={1.5}>
            {orders.map((order, index) => {
              const orderObjectId = order?._id;
              const displayOrderId = order?.orderId || order?.trackingNo || order?._id || '-';
              const customerName = getCustomerName(order);
              const productName = getProductName(order);
              const productImage = getProductImage(order, productLookup);
              const orderDate = formatDateDisplay(order?.createdAt || order?.date || order?.payment?.createdAt || order?.updatedAt);
              const paymentStatus = String(order?.payment?.status || order?.paymentStatus || order?.status || 'unknown').toLowerCase();
              const dispatchStatus = getDispatchStatus(order);
              const totalAmount = order?.amount?.total ?? order?.totalAmount ?? order?.amount ?? order?.price ?? 0;

              return (
                <Box
                  key={orderObjectId || `order-card-${index}`}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '72px 1fr', md: '96px 1fr auto' },
                    alignItems: { xs: 'flex-start', md: 'center' },
                    gap: { xs: 2, md: 3 },
                    bgcolor: 'background.paper',
                    borderRadius: 2,
                    px: { xs: 1.5, sm: 2 },
                    py: { xs: 1.25, sm: 1.5 },
                    border: '1px solid',
                    borderColor: 'divider',
                    boxShadow: '0 8px 24px rgba(15, 23, 42, 0.05)',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      boxShadow: '0 12px 30px rgba(15, 23, 42, 0.09)',
                      borderColor: 'grey.400'
                    }
                  }}
                >
                  <Avatar
                    variant="rounded"
                    src={productImage}
                    alt={productName === '-' ? 'Product' : productName}
                    sx={{
                      width: { xs: 72, md: 96 },
                      height: { xs: 72, md: 96 },
                      borderRadius: 2,
                      bgcolor: 'grey.100',
                      border: '1px solid',
                      borderColor: 'divider'
                    }}
                  >
                    <DiamondOutlinedIcon sx={{ color: 'text.secondary' }} />
                  </Avatar>

                  <Stack spacing={0.5} sx={{ minWidth: 0 }}>
                    <Box>
                      <Typography
                        component={RouterLink}
                        to={`/payments/orders/${orderObjectId}`}
                        variant="h5"
                        sx={{
                          fontWeight: 700,
                          textDecoration: 'none',
                          color: 'text.primary',
                          display: 'inline-block',
                          overflowWrap: 'anywhere',
                          '&:hover': { color: 'primary.main' }
                        }}
                      >
                        {displayOrderId}
                      </Typography>
                      <Typography variant="body1" color="text.secondary" sx={{ mt: 0.25 }}>
                        User: {customerName}
                      </Typography>
                      <Typography
                        variant="body1"
                        color="text.secondary"
                        sx={{
                          overflowWrap: 'anywhere',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden'
                        }}
                      >
                        Product: {productName}
                      </Typography>
                      <Typography variant="body1" color="text.secondary">
                        Date: {orderDate}
                      </Typography>
                    </Box>

                    <Stack direction="row" alignItems="center" spacing={1} sx={{ flexWrap: 'wrap', pt: 0.25 }}>
                      <Typography variant="body1" color="text.secondary">
                        Total: {formatMoney(order?.currency, totalAmount)}
                      </Typography>
                      <Chip
                        size="small"
                        label={paymentStatus}
                        sx={{
                          fontWeight: 700,
                          fontSize: '0.72rem',
                          height: 20,
                          borderRadius: 1,
                          bgcolor:
                            paymentStatus === 'captured'
                              ? '#2e7d32'
                              : paymentStatus === 'pending'
                              ? '#ed6c02'
                              : paymentStatus === 'failed'
                              ? '#d32f2f'
                              : 'grey.300',
                          color: ['captured', 'pending', 'failed'].includes(paymentStatus) ? '#fff' : 'inherit'
                        }}
                      />
                    </Stack>
                  </Stack>

                  <Stack
                    sx={{
                      gridColumn: { xs: '1 / -1', md: 'auto' },
                      alignItems: { xs: 'flex-start', md: 'flex-end' },
                      gap: 2,
                      justifySelf: { md: 'end' },
                      minWidth: { md: 150 }
                    }}
                  >
                    <Typography variant="body1" color="text.secondary" sx={{ fontWeight: 500 }}>
                      {dispatchStatus}
                    </Typography>
                    {orderObjectId ? (
                      <Button
                        variant="contained"
                        component={RouterLink}
                        to={`/payments/orders/${orderObjectId}`}
                        sx={{
                          bgcolor: 'common.black',
                          color: 'common.white',
                          borderRadius: 1.5,
                          px: 2.5,
                          py: 0.85,
                          textTransform: 'none',
                          fontWeight: 600,
                          '&:hover': { bgcolor: 'grey.900' }
                        }}
                      >
                        View Order
                      </Button>
                    ) : null}
                  </Stack>
                </Box>
              );
            })}

            {!orders.length && (
              <Box sx={{ bgcolor: 'background.paper', borderRadius: 2, p: 4, textAlign: 'center' }}>
                <Typography color="text.secondary">No payment orders found.</Typography>
              </Box>
            )}
          </Stack>

          <TablePagination
            component="div"
            count={Number(data?.total || 0)}
            page={page}
            onPageChange={(_, nextPage) => setPage(nextPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(event) => {
              setRowsPerPage(Number(event.target.value));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 20, 50, 100]}
            sx={{ mt: 1 }}
          />
        </MainCard>
      )}
    </Stack>
  );
}
